import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  PLATFORM_ID,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { CurrencyPipe, isPlatformBrowser } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BookingService } from '../../../services/booking.service';
import { PaymentBlock, PaymentStatus } from '../../../model/chat.model';

/**
 * Renders the prepay (TeeFox) payment surface inline in a booking-confirmation widget.
 *
 * `status` drives what shows:
 *  - pending + iframeable url  → TeeFox iframe + countdown timer
 *  - pending + non-iframeable  → "Complete Payment" redirect button
 *  - pending + no url yet       → "Securing your slot" placeholder
 *  - expired / cancelled        → soft terminal copy
 *  - paid / refunded / n/a      → nothing (the parent shows the normal confirmation UI)
 *
 * While pending, it polls GET /api/bookings/:id every 5s, listens for the TeeFox iframe's
 * postMessage events for instant feedback, and refetches when the hold countdown hits zero.
 * v1 has no reverse socket push, so polling + postMessage are the only signals.
 */
@Component({
  selector: 'app-payment-block',
  imports: [CurrencyPipe],
  templateUrl: './payment-block.component.html',
  styleUrl: './payment-block.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentBlockComponent implements OnInit {
  private readonly _bookingService = inject(BookingService);
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** Seed payment block from the widget payload. */
  readonly payment = input.required<PaymentBlock>();
  /** Mongo _id used for the poll (GET /api/bookings/:id). Polling is skipped when absent. */
  readonly bookingId = input<string | undefined>(undefined);
  /** When false (stale chat message), the redirect button is disabled. */
  readonly interactive = input<boolean>(true);

  /** Emits the latest payment block whenever the live status transitions. */
  readonly paymentChange = output<PaymentBlock>();
  /** Emits once the payment reaches a terminal state (paid / expired / cancelled / refunded). */
  readonly resolved = output<{ status: PaymentStatus; confirmationNumber?: string | null }>();
  /** User tapped "Retry payment" on a failed/expired card — parent re-initiates the booking. */
  readonly retry = output<void>();

  /** Live payment state — seeded from the input, then updated by the poll loop / postMessage. */
  protected readonly current = linkedSignal<PaymentBlock>(() => this.payment());
  protected readonly status = computed<PaymentStatus>(() => this.current().status);

  /** `mm:ss` (or "Expired") countdown until the TeeFox hold lapses. */
  protected readonly remaining = signal<string>('');

  protected readonly isIframeable = computed<boolean>(() => {
    const url = this.current().url;
    if (!url) return false;
    try {
      // TeeFox hosts (*.teefox.golf / *.teefox.com) don't send X-Frame-Options: DENY.
      return /\.teefox\.(golf|com)$/.test(new URL(url).hostname);
    } catch {
      return false;
    }
  });

  protected readonly safeUrl = computed<SafeResourceUrl | null>(() => {
    if (!this.isIframeable()) return null;
    return this._sanitizer.bypassSecurityTrustResourceUrl(this.current().url);
  });

  private _lastStatus?: PaymentStatus;
  private _countdownTimer?: ReturnType<typeof setInterval>;
  private readonly _onMessage = (event: MessageEvent): void => this._handleMessage(event);

  ngOnInit(): void {
    // Required inputs aren't available in the constructor, so seed transition tracking here.
    this._lastStatus = this.payment().status;
    if (this._isBrowser) {
      // No background polling: we refetch the booking only when the TeeFox iframe posts a payment
      // event (see _handleMessage) or when the hold countdown expires. The 1s timer below is
      // display-only (no network) for the countdown.
      this._tickCountdown();
      this._countdownTimer = setInterval(() => this._tickCountdown(), 1000);
      window.addEventListener('message', this._onMessage);
      this._destroyRef.onDestroy(() => {
        if (this._countdownTimer) clearInterval(this._countdownTimer);
        window.removeEventListener('message', this._onMessage);
      });
    }
  }

  /** Called by the iframe (onPaymentEvent) and by the poll — re-reads the booking's payment block. */
  protected refetch(): void {
    this._maybeRefetch(true);
  }

  protected onRetry(): void {
    if (this.interactive()) this.retry.emit();
  }

  protected openRedirect(): void {
    if (!this._isBrowser || !this.interactive()) return;
    const url = this.current().url;
    if (url) window.location.href = url;
  }

  /** TeeFox iframe posts these when payment completes / fails / is cancelled. */
  private _handleMessage(event: MessageEvent): void {
    const type = (event.data as { type?: string } | undefined)?.type;
    if (!type) return;
    const relevant = [
      'PAYMENT_SUCCESS',
      '3DS_COMPLETE',
      'GOLFMANAGER_PAYMENT_SUCCESS',
      'GOLFMANAGER_PAYMENT_FAILED',
      'GOLFMANAGER_PAYMENT_CANCELLED',
      'GOLFMANAGER_PAYMENT_EXPIRED',
    ];
    if (relevant.includes(type)) this.refetch();
  }

  private _maybeRefetch(force = false): void {
    if (!this._isBrowser) return;
    // Only the active (last, interactive) confirmation polls — stale chat widgets must go quiet.
    if (!this.interactive()) return;
    if (!force && this.current().status !== 'pending') return;
    const id = this.bookingId();
    if (!id) return;
    this._bookingService.loadBookingById(id).subscribe({
      next: (booking) => {
        const p = booking.payment;
        if (p) this._apply(p as PaymentBlock, booking.confirmationCode ?? null);
      },
      error: () => {
        /* transient poll failure — keep the current state, next tick retries */
      },
    });
  }

  private _apply(next: PaymentBlock, confirmationNumber: string | null): void {
    this.current.set(next);
    if (next.status !== this._lastStatus) {
      this._lastStatus = next.status;
      this.paymentChange.emit(next);
      if (next.status !== 'pending') {
        this.resolved.emit({ status: next.status, confirmationNumber });
      }
    }
  }

  private _tickCountdown(): void {
    const expiresAt = this.current().expiresAt;
    if (!expiresAt || this.current().status !== 'pending') {
      if (this.remaining() !== '') this.remaining.set('');
      return;
    }
    const label = this._remainingString(new Date(expiresAt));
    this.remaining.set(label);
    if (label === 'Expired') {
      // Hold lapsed: check the backend once, then fall back to 'expired' locally so the poll loop
      // stops (status is no longer 'pending') and the retry card shows even if the BE lags.
      this.refetch();
      this._apply({ ...this.current(), status: 'expired' }, null);
    }
  }

  private _remainingString(expiresAt: Date): string {
    const ms = expiresAt.getTime() - Date.now();
    if (Number.isNaN(ms) || ms <= 0) return 'Expired';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
}
